import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import Layout, { StatusBadge } from '../components/Layout'

const API = import.meta.env.VITE_API as string

interface Video { id: string; title: string; file_path: string; file_name: string; review_status: string | null; description: string | null }
interface Comment { id: string; user_name: string | null; comment_text: string; is_resolved: boolean; created_at: string; user_id: string | null }

const REVIEW_STATES = ['en_attente', 'en_revision', 'approuve', 'change_demande']

export default function Revue() {
  const { id } = useParams()
  const { profile, isAdmin } = useAuth()
  const [video, setVideo] = useState<Video | null>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [text, setText] = useState('')
  const [jwt, setJwt] = useState('')
  const [loading, setLoading] = useState(true)

  async function loadComments(vid: string) {
    const { data } = await supabase.from('video_comments').select('id,user_name,comment_text,is_resolved,created_at,user_id')
      .eq('video_id', vid).order('created_at', { ascending: true })
    setComments((data as any) || [])
  }
  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession()
      setJwt(sess.session?.access_token || '')
      const { data } = await supabase.from('videos').select('id,title,file_path,file_name,review_status,description').eq('id', id).maybeSingle()
      setVideo(data as any)
      if (data) await loadComments((data as any).id)
      setLoading(false)
    })()
  }, [id])

  async function post(e: React.FormEvent) {
    e.preventDefault()
    if (!text.trim() || !video) return
    const { data: u } = await supabase.auth.getUser()
    const { data } = await supabase.from('video_comments')
      .insert({ video_id: video.id, user_id: u.user?.id, user_name: profile?.full_name || profile?.email, comment_text: text.trim() })
      .select().single()
    if (data) setComments(c => [...c, data as any]); setText('')
  }
  async function toggleResolved(c: Comment) {
    await supabase.from('video_comments').update({ is_resolved: !c.is_resolved }).eq('id', c.id)
    setComments(cs => cs.map(x => x.id === c.id ? { ...x, is_resolved: !x.is_resolved } : x))
  }
  async function setStatus(s: string) {
    if (!video) return
    await supabase.from('videos').update({ review_status: s }).eq('id', video.id)
    setVideo({ ...video, review_status: s })
  }

  if (loading) return <Layout><p className="text-acier">Chargement…</p></Layout>
  if (!video) return <Layout><p className="text-acier">Vidéo introuvable ou accès refusé.</p></Layout>

  const slash = video.file_path.indexOf('/')
  const slug = video.file_path.slice(0, slash), name = video.file_path.slice(slash + 1)
  const src = `${API}/deliverable?slug=${slug}&name=${encodeURIComponent(name)}&access_token=${jwt}`

  return (
    <Layout title={video.title}>
      <a href={isAdmin ? '/admin/livrables' : '/portail'} className="text-xs uppercase tracking-widest text-acier hover:text-orange">← Retour</a>

      <div className="mt-4 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <video src={src} controls className="w-full rounded border border-white/10 bg-black" />
          <div className="flex items-center gap-3 mt-3">
            <StatusBadge status={video.review_status} />
            {isAdmin && (
              <select className="bg-black/30 border border-white/10 rounded px-2 py-1 text-xs" value={video.review_status || 'en_attente'} onChange={e => setStatus(e.target.value)}>
                {REVIEW_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>
        </div>

        <div>
          <h2 className="font-display text-xl tracking-wide mb-3">Commentaires</h2>
          <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
            {comments.length === 0 && <p className="text-acier text-sm">Aucun commentaire. Lancez la revue ci-dessous.</p>}
            {comments.map(c => (
              <div key={c.id} className={`border rounded p-3 text-sm ${c.is_resolved ? 'border-green-500/30 bg-green-500/5' : 'border-white/10'}`}>
                <div className="flex justify-between items-center">
                  <span className="text-orange text-xs font-bold">{c.user_name || 'Utilisateur'}</span>
                  <span className="text-acier text-[10px]">{new Date(c.created_at).toLocaleDateString('fr-CA')}</span>
                </div>
                <p className="mt-1">{c.comment_text}</p>
                <button onClick={() => toggleResolved(c)} className="text-[10px] uppercase tracking-widest text-acier hover:text-orange mt-2">
                  {c.is_resolved ? '✓ Résolu' : 'Marquer résolu'}
                </button>
              </div>
            ))}
          </div>
          <form onSubmit={post} className="mt-4">
            <textarea className="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm focus:border-orange outline-none min-h-[70px]"
              value={text} onChange={e => setText(e.target.value)} placeholder="Votre commentaire…" />
            <button className="mt-2 bg-orange hover:bg-orange-hover px-5 py-2 rounded-sm text-sm font-bold uppercase tracking-wider w-full">Publier</button>
          </form>
        </div>
      </div>
    </Layout>
  )
}
